import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Description,
  EmailOutlined,
  MoreVert,
  Person,
  PlayArrow,
  SkipNext,
  Undo,
  Upload,
} from '@mui/icons-material';
import { MouseEvent, useState } from 'react';
import { InvestmentChecklistTaskDTO, UserRole } from '@inversor-hub/types';
import { useSnackbar } from 'notistack';
import { useUpdateChecklistTask } from '@/lib/api/investment-checklist';
import { useGetMe } from '@/lib/api/me';
import ChecklistDocumentDialog from './ChecklistDocumentDialog';
import NotaryEmailDraftDialog from './NotaryEmailDraftDialog';
import TaskDueDateBadge from './TaskDueDateBadge';
import TaskStatusChip from './TaskStatusChip';

interface TaskCardProps {
  task: InvestmentChecklistTaskDTO;
  investmentId: string;
}

function getAssigneeIcon(assignedTo: UserRole) {
  return assignedTo === 'investor' ? Person : Business;
}

const TaskCard = ({ task, investmentId }: TaskCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(task.status === 'PENDING');
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [notaryEmailDialogOpen, setNotaryEmailDialogOpen] = useState(false);
  const { mutate: updateTask, isPending } = useUpdateChecklistTask(investmentId, task.id);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { data: userData } = useGetMe();

  const canUserComplete = userData?.role === task.assignedTo;
  const assigneeLabel = task.assignedTo === 'investor' ? 'Inversor' : 'PSI';
  const AssigneeIcon = getAssigneeIcon(task.assignedTo);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = (status: 'DONE' | 'SKIPPED' | 'PENDING') => {
    const previousStatus = task.status;
    updateTask(
      { status },
      {
        onSuccess: () => {
          const statusLabels = {
            DONE: 'completada',
            SKIPPED: 'omitida',
            PENDING: 'pendiente',
          };

          enqueueSnackbar(`Tarea ${statusLabels[status]}`, {
            variant: status === 'DONE' ? 'success' : status === 'SKIPPED' ? 'info' : 'default',
            action: (key) => (
              <Button
                size="small"
                color="inherit"
                startIcon={<Undo />}
                onClick={() => {
                  updateTask({ status: previousStatus });
                  closeSnackbar(key);
                }}
              >
                Deshacer
              </Button>
            ),
          });

          if (status !== 'PENDING') {
            setExpanded(false);
          }
        },
        onError: () => {
          enqueueSnackbar('Error al actualizar la tarea', { variant: 'error' });
        },
      }
    );
    handleMenuClose();
  };

  const getTaskTypeIcon = () => {
    return task.kind === 'DOC' ? <Description fontSize="small" /> : <PlayArrow fontSize="small" />;
  };

  const getTaskTypeLabel = () => {
    return task.kind === 'DOC' ? 'Documento' : 'Acción';
  };

  const isCompleted = task.status === 'DONE';
  const isSkipped = task.status === 'SKIPPED';
  const isNotaryEmailTask = task.slug === 'email-notaria';
  const documentCount = task.documents.length;
  const canOpenDocumentDialog =
    task.kind === 'DOC' && (canUserComplete || documentCount > 0) && !isSkipped;
  const documentButtonLabel = !canUserComplete
    ? 'Ver documentos'
    : documentCount > 0
      ? 'Gestionar documentos'
      : task.allowsMultipleDocuments
        ? 'Subir documentos'
        : 'Subir documento';

  const documentDialog = task.kind === 'DOC' && (
    <ChecklistDocumentDialog
      open={documentDialogOpen}
      onClose={() => setDocumentDialogOpen(false)}
      task={task}
      investmentId={investmentId}
      canManageDocuments={canUserComplete}
    />
  );

  const notaryEmailDialog = isNotaryEmailTask && (
    <NotaryEmailDraftDialog
      open={notaryEmailDialogOpen}
      investmentId={investmentId}
      onClose={() => setNotaryEmailDialogOpen(false)}
    />
  );

  if ((isCompleted || isSkipped) && !expanded) {
    return (
      <>
        <Card
          sx={{
            mb: 2,
            opacity: 0.7,
            transition: 'all 0.2s',
            '&:hover': {
              opacity: 1,
              boxShadow: 2,
            },
          }}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box
              display="flex"
              alignItems="center"
              gap={{ xs: 1.5, sm: 2 }}
              sx={{ flexWrap: 'wrap' }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                onClick={() => setExpanded(true)}
                sx={{ flex: '1 1 220px', minWidth: 0, cursor: 'pointer' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: isCompleted ? 'success.100' : 'grey.200',
                    color: isCompleted ? 'success.main' : 'text.secondary',
                  }}
                >
                  {isCompleted ? <CheckCircle /> : <SkipNext />}
                </Box>
                <Box flex={1} minWidth={0}>
                  <Tooltip title={task.label}>
                    <Typography variant="body1" fontWeight={500} noWrap>
                      {task.label}
                    </Typography>
                  </Tooltip>
                  {task.kind === 'DOC' && documentCount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {documentCount}{' '}
                      {documentCount === 1 ? 'documento adjunto' : 'documentos adjuntos'}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  minWidth: 0,
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  '& .MuiButton-root': { flexShrink: 0, whiteSpace: 'nowrap' },
                  '& .MuiChip-root': { flexShrink: 0 },
                }}
              >
                {canOpenDocumentDialog && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Upload />}
                    onClick={() => setDocumentDialogOpen(true)}
                    disabled={isPending}
                  >
                    {documentButtonLabel}
                  </Button>
                )}
                {isNotaryEmailTask && canUserComplete && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmailOutlined />}
                    onClick={() => setNotaryEmailDialogOpen(true)}
                  >
                    Generar borrador
                  </Button>
                )}
                <TaskStatusChip status={task.status} />
              </Box>
            </Box>
          </CardContent>
        </Card>
        {documentDialog}
        {notaryEmailDialog}
      </>
    );
  }

  return (
    <>
      <Card
        sx={{
          mb: 2,
          borderLeft: task.required ? 4 : 0,
          borderColor: task.required ? 'primary.main' : 'transparent',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                <Chip
                  icon={getTaskTypeIcon()}
                  label={getTaskTypeLabel()}
                  size="small"
                  variant="outlined"
                />
                {task.required && (
                  <Chip label="Obligatorio" size="small" color="primary" variant="outlined" />
                )}
                <Chip
                  icon={<AssigneeIcon fontSize="small" />}
                  label={`Asignado a: ${assigneeLabel}`}
                  size="small"
                  color={canUserComplete ? 'primary' : 'default'}
                  variant="outlined"
                />
                {(isCompleted || isSkipped) && <TaskStatusChip status={task.status} />}
              </Box>

              <Typography
                variant="h6"
                fontWeight={600}
                mb={0.5}
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.3 }}
              >
                {task.label}
              </Typography>

              {task.dueAt && (
                <TaskDueDateBadge
                  dueAt={task.dueAt}
                  dueAnchor={task.dueAnchor}
                  dueOffsetDays={task.dueOffsetDays}
                />
              )}
            </Box>

            {(isCompleted || isSkipped) && (task.kind === 'ACTION' || isSkipped) && (
              <>
                <IconButton size="small" onClick={handleMenuOpen} disabled={isPending}>
                  <MoreVert />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  <MenuItem onClick={() => handleStatusChange('PENDING')}>
                    <Undo fontSize="small" sx={{ mr: 1 }} />
                    Revertir a pendiente
                  </MenuItem>
                  {isCompleted && task.kind === 'ACTION' && (
                    <MenuItem onClick={() => handleStatusChange('SKIPPED')}>
                      <SkipNext fontSize="small" sx={{ mr: 1 }} />
                      Marcar como omitida
                    </MenuItem>
                  )}
                  {isSkipped && task.kind === 'ACTION' && (
                    <MenuItem onClick={() => handleStatusChange('DONE')}>
                      <CheckCircle fontSize="small" sx={{ mr: 1 }} />
                      Marcar como completada
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Box>

          {isNotaryEmailTask && canUserComplete && task.status !== 'PENDING' && (
            <Box mt={2}>
              <Button
                variant="contained"
                startIcon={<EmailOutlined />}
                onClick={() => setNotaryEmailDialogOpen(true)}
              >
                Generar borrador
              </Button>
            </Box>
          )}

          {task.kind === 'DOC' && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              {canOpenDocumentDialog && (
                <Button
                  variant="contained"
                  startIcon={<Upload />}
                  onClick={() => setDocumentDialogOpen(true)}
                  disabled={isPending}
                >
                  {documentButtonLabel}
                </Button>
              )}
              {task.status === 'PENDING' && canUserComplete && (
                <Button
                  variant="outlined"
                  onClick={() => handleStatusChange('SKIPPED')}
                  disabled={isPending}
                >
                  Omitir
                </Button>
              )}
              {!canUserComplete && documentCount === 0 && (
                <Alert severity="info" sx={{ width: '100%' }}>
                  Esta tarea debe ser completada por el {assigneeLabel}.
                </Alert>
              )}
            </Box>
          )}

          {task.kind === 'ACTION' && task.status === 'PENDING' && (
            <Box mt={2}>
              {canUserComplete ? (
                <Box display="flex" gap={1} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  {isNotaryEmailTask && (
                    <Button
                      variant="contained"
                      startIcon={<EmailOutlined />}
                      onClick={() => setNotaryEmailDialogOpen(true)}
                    >
                      Generar borrador
                    </Button>
                  )}
                  <Button
                    variant={isNotaryEmailTask ? 'outlined' : 'contained'}
                    startIcon={<CheckCircle />}
                    onClick={() => handleStatusChange('DONE')}
                    disabled={isPending}
                  >
                    Completar tarea
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleStatusChange('SKIPPED')}
                    disabled={isPending}
                    sx={{ minWidth: 100 }}
                  >
                    Omitir
                  </Button>
                </Box>
              ) : (
                <Alert severity="info">
                  Esta tarea debe ser completada por el {assigneeLabel}.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      {documentDialog}
      {notaryEmailDialog}
    </>
  );
};

export default TaskCard;
